<script setup>
import { isSupportRole } from '../utils/runner-role.js'
import { onMounted, onUnmounted, ref, computed } from 'vue'
import { useResultsStore } from '../stores/results-fetcher.js'
import { useRoute } from 'vue-router'
import { ASSETS_BASE_URL } from '../config.js'

const LOOP_LENGTH = 20000

const resultsStore = useResultsStore()
const route = useRoute()
const eventAlias = route.params.eventAlias
resultsStore.fetchResults(eventAlias)

const now = ref(Date.now())
let refreshResultsTimer = null
let clockUpdateTimer = null

onMounted(() => {
  refreshResultsTimer = setInterval(() => {
    resultsStore.fetchResults(route.params.eventAlias)
  }, 30000)
  clockUpdateTimer = setInterval(() => {
    now.value = Date.now()
  }, 1000)
})

onUnmounted(() => {
  if (clockUpdateTimer) clearInterval(clockUpdateTimer)
  if (refreshResultsTimer) clearInterval(refreshResultsTimer)
})

const sortedResults = computed(() => {
  return resultsStore.results
    .filter(r => !isSupportRole(r))
    .slice()
    .sort((a, b) => (b.score || 0) - (a.score || 0))
})

function getLoopBoxes(score) {
  const s = score || 0
  const completedLoops = Math.floor(s / LOOP_LENGTH)
  const progressInCurrent = (s % LOOP_LENGTH) / LOOP_LENGTH
  const totalBoxes = Math.max(1, completedLoops + 1)
  const boxes = []
  for (let i = 0; i < totalBoxes; i++) {
    if (i < completedLoops) {
      boxes.push(100)
    } else {
      boxes.push(Math.round(progressInCurrent * 100))
    }
  }
  return boxes
}

function formatScore(score) {
  const km = (score || 0) / 1000
  if (km === Math.floor(km)) {
    return `${Math.floor(km)} km`
  }
  return `${km.toFixed(1)} km`
}
</script>

<template>
  <div>
    <div class="header-container">
      <img 
        v-if="resultsStore.event?.event?.logo_file" 
        :src="`${ASSETS_BASE_URL}/files/logo/${resultsStore.event.event.logo_file}`" 
        alt="Event Logo" 
        class="event-logo"
      />
      <h1 class="header-title">Nieoficjalne wyniki na żywo</h1>
    </div>
    <div class="table-scroll">
      <div class="settings">
        <span class="badge bg-info">Aktualizacja co 30 sekund</span>
        <span class="badge bg-success">Czas: {{ new Date(now).toLocaleTimeString() }}</span>
      </div>
      <table class="table table-striped">
        <thead>
          <tr>
            <th>#</th>
            <th>Zawodnik</th>
            <th>Pętle</th>
            <th>Dystans</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(result, index) in sortedResults"
            :key="result.id"
          >
            <td>{{ index + 1 }}</td>
            <td><span class="badge bg-secondary">{{ result.number }}</span> {{ result.name }}</td>
            <td class="loops-cell">
              <div class="loops-container">
                <div
                  v-for="(fillPercent, i) in getLoopBoxes(result.score)"
                  :key="i"
                  class="loop-box"
                >
                  <div class="loop-fill" :style="{ width: fillPercent + '%' }"></div>
                </div>
              </div>
            </td>
            <td>{{ formatScore(result.score) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.header-container {
  display: flex;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
  justify-content: center;
  padding: 10px 0;
}

.event-logo {
  max-height: 150px;
  max-width: auto;
  object-fit: contain;
}

.header-title {
  margin: 0;
}

.table {
  text-align: left;
}

.table-scroll {
  width: 100%;
  max-height: 80vh;
  overflow-x: auto;
  overflow-y: auto;
}

.table thead th {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #f8f9fa;
}

.loops-cell {
  vertical-align: middle;
}

.loops-container {
  display: flex;
  gap: 4px;
  align-items: center;
  flex-wrap: wrap;
}

.loop-box {
  width: 28px;
  height: 20px;
  border: 1px solid #6c757d;
  border-radius: 3px;
  background-color: #e9ecef;
  overflow: hidden;
  position: relative;
}

.loop-fill {
  height: 100%;
  background-color: #0d6efd;
  position: absolute;
  left: 0;
  top: 0;
}
</style>
